import React, { useState } from 'react';
import { Bed, Car, MapPin, Compass, Calendar, Users, Search, ExternalLink, Star, ChevronRight, Plane, Utensils } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

// ============================================
// AFFILIATE CONFIGURATION
// Update these with your actual affiliate IDs once approved
// ============================================
const AFFILIATE_CONFIG = {
  booking: {
    partnerId: 'YOUR_BOOKING_PARTNER_ID', // Get from Booking.com affiliate dashboard
    baseUrl: 'https://www.booking.com',
  },
  rentalcars: {
    affiliateId: 'YOUR_RENTALCARS_ID', // Get from Rentalcars.com affiliate dashboard
    baseUrl: 'https://www.rentalcars.com',
  },
  viator: {
    partnerId: 'YOUR_VIATOR_ID', // Get from Viator affiliate dashboard
    baseUrl: 'https://www.viator.com',
  },
  getYourGuide: {
    partnerId: 'YOUR_GYG_ID', // Get from GetYourGuide affiliate dashboard
    baseUrl: 'https://www.getyourguide.com',
  }
};

// City to location mapping for affiliate deep links
const CITY_MAPPINGS = {
  'gold-coast': { booking: 'Gold+Coast', rentalcars: 'Gold-Coast', viator: 'Gold-Coast', state: 'Queensland' },
  'brisbane': { booking: 'Brisbane', rentalcars: 'Brisbane', viator: 'Brisbane', state: 'Queensland' },
  'sydney': { booking: 'Sydney', rentalcars: 'Sydney', viator: 'Sydney', state: 'New South Wales' },
  'melbourne': { booking: 'Melbourne', rentalcars: 'Melbourne', viator: 'Melbourne', state: 'Victoria' },
  'perth': { booking: 'Perth', rentalcars: 'Perth', viator: 'Perth', state: 'Western Australia' },
  'adelaide': { booking: 'Adelaide', rentalcars: 'Adelaide', viator: 'Adelaide', state: 'South Australia' },
  'darwin': { booking: 'Darwin', rentalcars: 'Darwin', viator: 'Darwin', state: 'Northern Territory' },
  'hobart': { booking: 'Hobart', rentalcars: 'Hobart', viator: 'Hobart', state: 'Tasmania' },
  'cairns': { booking: 'Cairns', rentalcars: 'Cairns', viator: 'Cairns', state: 'Queensland' },
  'sunshine-coast': { booking: 'Sunshine+Coast', rentalcars: 'Sunshine-Coast', viator: 'Sunshine-Coast', state: 'Queensland' },
};

// ============================================
// ACCOMMODATION BOOKING WIDGET
// ============================================
export const AccommodationBookingWidget = ({ cityId, cityName, variant = 'full' }) => {
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('2');
  
  const cityMapping = CITY_MAPPINGS[cityId] || { booking: cityName?.replace(/\s+/g, '+') || 'Australia' };
  
  const generateBookingUrl = () => {
    const baseUrl = AFFILIATE_CONFIG.booking.baseUrl;
    const params = new URLSearchParams({
      ss: cityMapping.booking + ', Australia',
      aid: AFFILIATE_CONFIG.booking.partnerId,
      lang: 'en-au',
      sb: '1',
      src_elem: 'sb',
      src: 'searchresults',
      dest_type: 'city',
      group_adults: guests,
      no_rooms: '1',
    });
    
    if (checkIn) params.append('checkin', checkIn);
    if (checkOut) params.append('checkout', checkOut);
    
    return `${baseUrl}/searchresults.html?${params.toString()}`;
  };

  const handleSearch = () => {
    window.open(generateBookingUrl(), '_blank');
  };

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Bed className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Book Accommodation</h3>
            <p className="text-sky-100 text-sm">Find the best stays in {cityName}</p>
          </div>
        </div>
        <Button 
          onClick={handleSearch}
          className="w-full bg-white text-sky-600 hover:bg-sky-50 font-semibold"
          data-testid="accommodation-search-compact"
        >
          Search Hotels <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-8 text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      <div className="relative">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Bed className="w-7 h-7" />
          </div>
          <div>
            <h3 className="font-bold text-2xl">Book Your Stay</h3>
            <p className="text-sky-100">Find hotels, resorts & apartments in {cityName}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm text-sky-100 mb-1">Check-in</label>
            <Input
              type="date"
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
              className="bg-white/20 border-white/30 text-white placeholder:text-white/60 [color-scheme:dark]"
              data-testid="accommodation-checkin"
            />
          </div>
          <div>
            <label className="block text-sm text-sky-100 mb-1">Check-out</label>
            <Input
              type="date"
              value={checkOut}
              onChange={(e) => setCheckOut(e.target.value)}
              className="bg-white/20 border-white/30 text-white placeholder:text-white/60 [color-scheme:dark]"
              data-testid="accommodation-checkout"
            />
          </div>
          <div>
            <label className="block text-sm text-sky-100 mb-1">Guests</label>
            <select
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
              className="w-full h-10 px-3 rounded-md bg-white/20 border border-white/30 text-white"
              data-testid="accommodation-guests"
            >
              <option value="1">1 Guest</option>
              <option value="2">2 Guests</option>
              <option value="3">3 Guests</option>
              <option value="4">4 Guests</option>
              <option value="5">5+ Guests</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button 
              onClick={handleSearch}
              className="w-full bg-white text-sky-600 hover:bg-sky-50 font-bold h-10"
              data-testid="accommodation-search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-sky-100">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-300" />
            Best price guarantee
          </span>
          <span className="flex items-center gap-1">
            <Bed className="w-4 h-4" />
            1000+ properties
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            Free cancellation options
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// CAR HIRE BOOKING WIDGET
// ============================================
export const CarHireBookingWidget = ({ cityId, cityName, variant = 'full' }) => {
  const [pickupDate, setPickupDate] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  
  const cityMapping = CITY_MAPPINGS[cityId] || { rentalcars: cityName?.replace(/\s+/g, '-') || 'Australia' };
  
  const generateRentalUrl = () => {
    const baseUrl = AFFILIATE_CONFIG.rentalcars.baseUrl;
    // Deep link to search results
    return `${baseUrl}/search-results?country=Australia&location=${encodeURIComponent(cityMapping.rentalcars)}&affiliateCode=${AFFILIATE_CONFIG.rentalcars.affiliateId}`;
  };

  const handleSearch = () => {
    window.open(generateRentalUrl(), '_blank');
  };

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Car className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Hire a Car</h3>
            <p className="text-emerald-100 text-sm">Best rates in {cityName}</p>
          </div>
        </div>
        <Button 
          onClick={handleSearch}
          className="w-full bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
          data-testid="carhire-search-compact"
        >
          Compare Prices <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      <div className="relative">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Car className="w-7 h-7" />
          </div>
          <div>
            <h3 className="font-bold text-2xl">Hire a Car</h3>
            <p className="text-emerald-100">Compare prices from all major rental companies</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm text-emerald-100 mb-1">Pick-up Date</label>
            <Input
              type="date"
              value={pickupDate}
              onChange={(e) => setPickupDate(e.target.value)}
              className="bg-white/20 border-white/30 text-white placeholder:text-white/60 [color-scheme:dark]"
              data-testid="carhire-pickup"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-100 mb-1">Drop-off Date</label>
            <Input
              type="date"
              value={dropoffDate}
              onChange={(e) => setDropoffDate(e.target.value)}
              className="bg-white/20 border-white/30 text-white placeholder:text-white/60 [color-scheme:dark]"
              data-testid="carhire-dropoff"
            />
          </div>
          <div className="flex items-end">
            <Button 
              onClick={handleSearch}
              className="w-full bg-white text-emerald-600 hover:bg-emerald-50 font-bold h-10"
              data-testid="carhire-search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Compare
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-emerald-100">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-300" />
            No hidden fees
          </span>
          <span className="flex items-center gap-1">
            <Car className="w-4 h-4" />
            All major brands
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            Airport & city locations
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// TOURS & ACTIVITIES BOOKING WIDGET
// ============================================
export const ToursActivitiesWidget = ({ cityId, cityName, variant = 'full' }) => {
  const cityMapping = CITY_MAPPINGS[cityId] || { viator: cityName?.replace(/\s+/g, '-') || 'Australia' };
  
  const generateViatorUrl = () => {
    return `https://www.viator.com/Australia/d318-ttd?pid=${AFFILIATE_CONFIG.viator.partnerId}`;
  };

  const generateCategoryUrl = (category) => {
    const urls = {
      tours: `https://www.viator.com/searchResults/all?text=${encodeURIComponent(cityMapping.viator + ' tours')}`,
      activities: `https://www.viator.com/searchResults/all?text=${encodeURIComponent(cityMapping.viator + ' activities')}`,
      attractions: `https://www.viator.com/searchResults/all?text=${encodeURIComponent(cityMapping.viator + ' attractions')}`,
      daytrips: `https://www.viator.com/searchResults/all?text=${encodeURIComponent(cityMapping.viator + ' day trips')}`,
    };
    return urls[category] || generateViatorUrl();
  };

  const handleSearch = () => {
    window.open(generateViatorUrl(), '_blank');
  };

  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Compass className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Tours & Activities</h3>
            <p className="text-purple-100 text-sm">Things to do in {cityName}</p>
          </div>
        </div>
        <Button 
          onClick={handleSearch}
          className="w-full bg-white text-purple-600 hover:bg-purple-50 font-semibold"
          data-testid="tours-search-compact"
        >
          Explore Activities <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-8 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      <div className="relative">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Compass className="w-7 h-7" />
          </div>
          <div>
            <h3 className="font-bold text-2xl">Tours & Activities</h3>
            <p className="text-purple-100">Discover unforgettable experiences in {cityName}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <a 
            href={generateCategoryUrl('tours')}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center p-4 bg-white/10 hover:bg-white/20 rounded-xl transition cursor-pointer"
            data-testid="tours-category-tours"
          >
            <Compass className="w-8 h-8 mb-2" />
            <span className="font-medium text-sm">Tours</span>
          </a>
          <a 
            href={generateCategoryUrl('activities')}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center p-4 bg-white/10 hover:bg-white/20 rounded-xl transition cursor-pointer"
            data-testid="tours-category-activities"
          >
            <MapPin className="w-8 h-8 mb-2" />
            <span className="font-medium text-sm">Activities</span>
          </a>
          <a 
            href={generateCategoryUrl('attractions')}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center p-4 bg-white/10 hover:bg-white/20 rounded-xl transition cursor-pointer"
            data-testid="tours-category-attractions"
          >
            <Star className="w-8 h-8 mb-2" />
            <span className="font-medium text-sm">Attractions</span>
          </a>
          <a 
            href={generateCategoryUrl('daytrips')}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center p-4 bg-white/10 hover:bg-white/20 rounded-xl transition cursor-pointer"
            data-testid="tours-category-daytrips"
          >
            <Plane className="w-8 h-8 mb-2" />
            <span className="font-medium text-sm">Day Trips</span>
          </a>
        </div>

        <Button 
          onClick={handleSearch}
          className="w-full bg-white text-purple-600 hover:bg-purple-50 font-bold"
          data-testid="tours-search-btn"
        >
          <Search className="w-4 h-4 mr-2" />
          Browse All {cityName} Experiences
        </Button>

        <div className="flex flex-wrap items-center gap-4 text-sm text-purple-100 mt-4">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-300" />
            Verified reviews
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Free cancellation
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Skip-the-line tickets
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMBINED AFFILIATE SECTION
// ============================================
export const AffiliateBookingSection = ({ cityId, cityName }) => {
  return (
    <section className="py-12 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            Plan Your {cityName} Trip
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Book accommodation, hire a car, and find amazing tours & activities all in one place
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <AccommodationBookingWidget cityId={cityId} cityName={cityName} variant="compact" />
          <CarHireBookingWidget cityId={cityId} cityName={cityName} variant="compact" />
          <ToursActivitiesWidget cityId={cityId} cityName={cityName} variant="compact" />
        </div>
      </div>
    </section>
  );
};

// ============================================
// DINING WIDGET (Bonus for Food & Dining pages)
// ============================================
export const DiningBookingWidget = ({ cityId, cityName }) => {
  const handleSearch = () => {
    window.open(`https://www.thefork.com.au/search?cityId=Melbourne`, '_blank');
  };

  return (
    <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 text-white">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
          <Utensils className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-bold text-lg">Book a Restaurant</h3>
          <p className="text-orange-100 text-sm">Reserve a table in {cityName}</p>
        </div>
      </div>
      <Button 
        onClick={handleSearch}
        className="w-full bg-white text-orange-600 hover:bg-orange-50 font-semibold"
        data-testid="dining-search-btn"
      >
        Find Restaurants <ExternalLink className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

export default {
  AccommodationBookingWidget,
  CarHireBookingWidget,
  ToursActivitiesWidget,
  AffiliateBookingSection,
  DiningBookingWidget
};
